import { useNationality } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useNationality";
import { ICategoryGroup } from "@faire/deprecated-retailer-visitor-shared/views/brands/landing-page/const";
import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Avatar } from "@faire/web/slate/deprecated/Avatar";
import { Row, Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import React from "react";
import styled from "styled-components";

export const BrandsLandingPageTestimonialTile: React.FC<{
  category: ICategoryGroup;
  isMobile?: boolean;
  isPersonalized?: boolean;
}> = ({ category, isMobile, isPersonalized }) => {
  const isSSR = useIsSSR();
  if (isMobile) {
    return (
      <Column style={{ width: "fit-content" }}>
        <Image
          src={optimizeImageUrl(
            {
              url: category.featureImageUrl,
            },
            isSSR,
            {
              width: 312,
              height: 172,
              resizeMethod: "fill",
            }
          )}
          width={312}
          height={172}
        />
        <TestimonialContent
          category={category}
          isPersonalized={isPersonalized}
        />
      </Column>
    );
  }

  return (
    <Row style={{ height: "fit-content" }}>
      <ContentColumn
        $bgColor={
          isPersonalized ? Core.surface.inverse : category.backgroundColor
        }
        $opacity={isPersonalized ? undefined : (category.opacity ?? 0) / 2}
        justify="center"
      >
        <img src={optimizeImageUrl({ url: category.featureImageUrl }, isSSR)} />
      </ContentColumn>
      <TestimonialContent category={category} isPersonalized={isPersonalized} />
    </Row>
  );
};

const TestimonialContent: React.FC<{
  category: ICategoryGroup;
  isPersonalized?: boolean;
}> = ({ category, isPersonalized }) => {
  const { isInternational: isInternationalFromRetailer } = useNationality();
  const isSSR = useIsSSR();
  const isInternational: boolean =
    isInternationalFromRetailer &&
    isNotUndefined(category.internationalTestimonialData);

  const brandLogoUrl =
    isInternational && category.internationalTestimonialData?.logoUrl
      ? category.internationalTestimonialData.logoUrl
      : category.brandLogoUrl;

  return (
    <TestimonialContentWrapper
      justify="space-between"
      $bgColor={
        isPersonalized ? Core.surface.inverse : category.backgroundColor
      }
      $opacity={isPersonalized ? undefined : category.opacity}
    >
      <Column>
        {isInternational &&
        category.internationalTestimonialData?.brandName &&
        !category.internationalTestimonialData.logoUrl ? (
          <Typography variant="pageHeaderSerifRegular">
            {category.internationalTestimonialData?.brandName}
          </Typography>
        ) : (
          <img
            style={{ width: "fit-content" }}
            src={optimizeImageUrl({ url: brandLogoUrl }, isSSR)}
          />
        )}
        <Spacer height="2x" />
        <Typography variant="subheadingSansRegular">
          {isInternational
            ? category.internationalTestimonialData?.testimonial
            : category.testimonial}
        </Typography>
      </Column>
      <Spacer height="4x" />
      <Column>
        <Row align="center">
          <Avatar
            size="small"
            src={
              isInternational
                ? category.internationalTestimonialData?.profileImageUrl
                : category.profileImageUrl
            }
          />
          <Spacer width="2x" />
          <Column>
            <Typography variant="paragraphSansMedium">
              {isInternational
                ? category.internationalTestimonialData?.brandOwnerName
                : category.brandOwnerName}
            </Typography>
            <Typography>
              {isInternational
                ? category.internationalTestimonialData?.nameSubtitle
                : category.brandOwnerNameSubtitle}
            </Typography>
          </Column>
        </Row>
      </Column>
    </TestimonialContentWrapper>
  );
};

const ContentColumn = styled(Column).attrs({ flex: 1 })<{
  $bgColor: string;
  $opacity?: number;
}>`
  background-color: ${(props) => props.$bgColor}${(props) => props.$opacity};
  width: 312px;
  ${responsiveMediaQueries.tabletAndAbove} {
    width: 410px;
  }
  transition: background-color 0.3s ease-in-out;
`;

const TestimonialContentWrapper = styled(ContentColumn)`
  padding: ${getSpacing("5x")};
`;

const Image = styled.img`
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
`;
